import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Archive, ArchiveRestore, KeyRound, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { tapHaptic } from '../../shared/lib/haptics';
import { Button } from '../../shared/ui/Button';
import { Input } from '../../shared/ui/Input';
import { PageHeader } from '../../shared/ui/PageHeader';
import { Select } from '../../shared/ui/Select';
import { Skeleton } from '../../shared/ui/Skeleton';
import { StatusBanner } from '../../shared/ui/StatusBanner';
import { useToast } from '../../shared/ui/toast-context';

import { AddStudentDialog } from './AddStudentDialog';
import {
  getCurrentInstitute,
  INSTITUTE_QUERY_KEYS,
  listBatchMemberships,
  listInstituteBatches,
  listInstituteStudents,
  setStudentArchived,
  type InstituteStudentRow,
} from './api';
import { ArchiveStudentDialog } from './ArchiveStudentDialog';
import { ImportStudentsDialog } from './ImportStudentsDialog';
import { ResetPasswordDialog, type ResetTarget } from './ResetPasswordDialog';
import s from './StudentsPage.module.css';

function initialsFromNameOrEmail(name: string, email: string): string {
  const source = name.length > 0 ? name : (email.split('@')[0] ?? '?');
  const parts = source.split(/[\s._-]/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function displayNameFor(row: InstituteStudentRow): string {
  return row.display_name === '' ? row.email : row.display_name;
}

type ExamFilter = 'all' | 'academic' | 'general';
type StatusFilter = 'all' | 'invited' | 'joined';
/** 'all', 'none' (in no batch at all), or one batch's id. */
type BatchFilter = string;

const NO_BATCHES: ReadonlySet<string> = new Set();

const SKELETON_ROWS = ['a', 'b', 'c', 'd', 'e'] as const;

/** Five rows shaped like the real ones — avatar plus two lines — in the real
 *  list classes, so the page does not jump when the roster lands. */
function StudentsSkeleton(): JSX.Element {
  return (
    <div className={s.list} role="status" aria-label="Loading students">
      {SKELETON_ROWS.map((key) => (
        <div key={key} className={s.row}>
          <Skeleton variant="circle" width={36} height={36} />
          <div className={s.rowMain}>
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="text" width="65%" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function InstituteStudentsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { show } = useToast();
  const [search, setSearch] = useState('');
  const [examFilter, setExamFilter] = useState<ExamFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [batchFilter, setBatchFilter] = useState<BatchFilter>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<InstituteStudentRow | null>(null);
  const [resetTarget, setResetTarget] = useState<ResetTarget | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const current = useQuery({
    queryKey: INSTITUTE_QUERY_KEYS.current,
    queryFn: getCurrentInstitute,
  });
  const instituteId = current.data?.institute_id ?? null;

  const list = useQuery({
    queryKey:
      instituteId !== null
        ? INSTITUTE_QUERY_KEYS.studentsWithArchived(instituteId)
        : ['institute', 'students', 'disabled'],
    // Pull archived rows too — the roster owns the active/archived split.
    queryFn: () => listInstituteStudents(instituteId as string, true),
    enabled: instituteId !== null,
  });

  const batches = useQuery({
    queryKey:
      instituteId !== null
        ? INSTITUTE_QUERY_KEYS.batches(instituteId)
        : ['institute', 'batches', 'disabled'],
    queryFn: () => listInstituteBatches(instituteId as string),
    enabled: instituteId !== null,
  });
  const batchList = useMemo(() => batches.data ?? [], [batches.data]);

  const memberships = useQuery({
    queryKey:
      instituteId !== null
        ? INSTITUTE_QUERY_KEYS.batchMemberships(instituteId)
        : ['institute', 'batch_memberships', 'disabled'],
    queryFn: () => listBatchMemberships(instituteId as string),
    // No batches, nothing to be a member of.
    enabled: instituteId !== null && batchList.length > 0,
  });

  const batchesByStudent = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const m of memberships.data ?? []) {
      const set = map.get(m.student_id);
      if (set === undefined) map.set(m.student_id, new Set([m.batch_id]));
      else set.add(m.batch_id);
    }
    return map;
  }, [memberships.data]);

  // The batch filter only bites once membership has landed: an empty map
  // would put every student in "Not in any batch". A batch deleted while it
  // was selected falls back to 'all' rather than matching nobody.
  const batchFilterReady = memberships.data !== undefined && batchList.length > 0;
  const effectiveBatch: BatchFilter = !batchFilterReady
    ? 'all'
    : batchFilter === 'all' || batchFilter === 'none'
      ? batchFilter
      : batchList.some((b) => b.id === batchFilter)
        ? batchFilter
        : 'all';

  const archiveMutation = useMutation({
    mutationFn: ({ studentId, archived }: { studentId: string; archived: boolean; name: string }) =>
      setStudentArchived(studentId, archived),
    onSuccess: (_result, { archived, name }) => {
      setConfirmTarget(null);
      setActionError(null);
      // The row moves between the active list and the archived section, which
      // on a phone is usually off screen — say what happened. Errors stay inline.
      tapHaptic();
      show(archived ? `Archived ${name}` : `Restored ${name}`, { tone: 'success' });
      if (instituteId !== null) {
        // Refresh roster (both the active-only picker cache and this view),
        // the dashboard count, and batches (membership/counts changed).
        void queryClient.invalidateQueries({
          queryKey: ['institute', 'students', instituteId],
        });
        void queryClient.invalidateQueries({
          queryKey: INSTITUTE_QUERY_KEYS.stats.studentsCount(instituteId),
        });
        void queryClient.invalidateQueries({
          queryKey: INSTITUTE_QUERY_KEYS.batches(instituteId),
        });
      }
    },
    onError: (err: Error) => setActionError(err.message),
  });

  // Everything except the batch filter — kept apart so the empty state can
  // tell "nobody matches" from "everybody who matches is already in a batch".
  const matchesOtherFilters = useCallback(
    (row: InstituteStudentRow): boolean => {
      if (examFilter !== 'all' && row.exam_type !== examFilter) return false;
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      const q = search.trim().toLowerCase();
      if (q === '') return true;
      return (
        row.email.toLowerCase().includes(q) ||
        row.display_name.toLowerCase().includes(q) ||
        (row.roll_no?.toLowerCase().includes(q) ?? false) ||
        (row.login_handle?.toLowerCase().includes(q) ?? false) ||
        row.phone.toLowerCase().includes(q)
      );
    },
    [examFilter, statusFilter, search],
  );

  const matches = useCallback(
    (row: InstituteStudentRow): boolean => {
      if (effectiveBatch !== 'all') {
        const inBatches = batchesByStudent.get(row.id) ?? NO_BATCHES;
        if (effectiveBatch === 'none' ? inBatches.size > 0 : !inBatches.has(effectiveBatch)) {
          return false;
        }
      }
      return matchesOtherFilters(row);
    },
    [effectiveBatch, batchesByStudent, matchesOtherFilters],
  );

  const allRows = useMemo(() => list.data ?? [], [list.data]);
  const active = useMemo(() => allRows.filter((r) => r.archived_at === null), [allRows]);
  const archived = useMemo(() => allRows.filter((r) => r.archived_at !== null), [allRows]);
  const activeFiltered = useMemo(() => active.filter(matches), [active, matches]);
  const archivedFiltered = useMemo(() => archived.filter(matches), [archived, matches]);

  const invitedCount = useMemo(() => active.filter((r) => r.status === 'invited').length, [active]);

  // Counted over ACTIVE students only: archiving pulls a student out of every
  // batch, so an archived row would always count as "not in any batch".
  const batchCounts = useMemo(() => {
    const perBatch = new Map<string, number>();
    let unbatched = 0;
    for (const row of active) {
      const inBatches = batchesByStudent.get(row.id) ?? NO_BATCHES;
      if (inBatches.size === 0) unbatched += 1;
      for (const id of inBatches) perBatch.set(id, (perBatch.get(id) ?? 0) + 1);
    }
    return { perBatch, unbatched };
  }, [active, batchesByStudent]);

  const filtersActiveBesidesBatch =
    search.trim() !== '' || statusFilter !== 'all' || examFilter !== 'all';
  const filtersActive = filtersActiveBesidesBatch || effectiveBatch !== 'all';
  // "Not in any batch" came back empty, yet the other filters alone match
  // someone: that is an answer, not a dead end.
  const everyoneBatched =
    effectiveBatch === 'none' && activeFiltered.length === 0 && active.some(matchesOtherFilters);

  function clearFilters(): void {
    setSearch('');
    setStatusFilter('all');
    setExamFilter('all');
    setBatchFilter('all');
  }

  const pendingId =
    archiveMutation.isPending && archiveMutation.variables !== undefined
      ? archiveMutation.variables.studentId
      : null;

  function requestArchive(row: InstituteStudentRow): void {
    setActionError(null);
    setConfirmTarget(row);
  }
  function confirmArchive(): void {
    if (confirmTarget === null) return;
    archiveMutation.mutate({
      studentId: confirmTarget.id,
      archived: true,
      name: displayNameFor(confirmTarget),
    });
  }
  function restore(row: InstituteStudentRow): void {
    setActionError(null);
    archiveMutation.mutate({ studentId: row.id, archived: false, name: displayNameFor(row) });
  }
  function requestReset(row: InstituteStudentRow): void {
    setResetTarget({ id: row.id, name: displayNameFor(row), username: row.login_handle ?? '' });
  }

  if (current.isPending) {
    return <StudentsSkeleton />;
  }
  if (current.error !== null) {
    return <StatusBanner tone="error">{current.error.message}</StatusBanner>;
  }
  if (instituteId === null) {
    return (
      <StatusBanner tone="error">
        No active institute membership found for your account.
      </StatusBanner>
    );
  }

  const total = active.length;
  const showingFiltered = activeFiltered.length !== total;
  const hasAnyStudents = active.length > 0 || archived.length > 0;

  return (
    <div className={s.root}>
      <PageHeader
        eyebrow="Roster"
        title="Students"
        subtitle="Create a username + password login (no email needed) or send an email magic-link. Add one at a time or import a CSV."
        actions={
          <>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              Import CSV
            </Button>
            <Button onClick={() => setAddOpen(true)}>Add student</Button>
          </>
        }
      />

      {actionError !== null ? <StatusBanner tone="error">{actionError}</StatusBanner> : null}

      {hasAnyStudents ? (
        <div className={s.toolbar}>
          <div className={s.searchWrap}>
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, roll #, phone…"
              aria-label="Search students"
            />
          </div>
          <div className={s.filterWrap}>
            <label className={s.filterLabel} htmlFor="status_filter">
              Status
            </label>
            <Select
              id="status_filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">All</option>
              <option value="invited">Invited{invitedCount > 0 ? ` (${invitedCount})` : ''}</option>
              <option value="joined">Joined</option>
            </Select>
          </div>
          <div className={s.filterWrap}>
            <label className={s.filterLabel} htmlFor="exam_filter">
              Exam type
            </label>
            <Select
              id="exam_filter"
              value={examFilter}
              onChange={(e) => setExamFilter(e.target.value as ExamFilter)}
            >
              <option value="all">All</option>
              <option value="academic">Academic</option>
              <option value="general">General</option>
            </Select>
          </div>
          {batches.isPending || batchList.length > 0 ? (
            <div className={s.filterWrap}>
              <label className={s.filterLabel} htmlFor="batch_filter">
                Batch
              </label>
              <Select
                id="batch_filter"
                value={effectiveBatch}
                disabled={!batchFilterReady}
                aria-busy={batches.isPending || memberships.isPending ? true : undefined}
                onChange={(e) => setBatchFilter(e.target.value)}
              >
                <option value="all">All batches</option>
                {batchFilterReady ? (
                  <>
                    <option value="none">Not in any batch ({batchCounts.unbatched})</option>
                    <optgroup label="Batches">
                      {batchList.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name} ({batchCounts.perBatch.get(b.id) ?? 0})
                        </option>
                      ))}
                    </optgroup>
                  </>
                ) : null}
              </Select>
            </div>
          ) : null}
        </div>
      ) : null}

      {hasAnyStudents && (batches.error !== null || memberships.error !== null) ? (
        <StatusBanner tone="warning">
          Couldn’t load batch membership, so the batch filter is off.{' '}
          {(batches.error ?? memberships.error)?.message}
        </StatusBanner>
      ) : null}

      {list.isPending ? (
        <StudentsSkeleton />
      ) : list.error !== null ? (
        <StatusBanner tone="error">{list.error.message}</StatusBanner>
      ) : !hasAnyStudents ? (
        <div className={s.empty}>
          <h2 className={s.emptyTitle}>No students yet</h2>
          <p className={s.emptyBody}>
            Invite your first student or import a roster from CSV to get going.
          </p>
          <div className={s.emptyActions}>
            <Button onClick={() => setAddOpen(true)}>Add student</Button>
            <Button variant="ghost" onClick={() => setImportOpen(true)}>
              Import CSV
            </Button>
          </div>
        </div>
      ) : (
        <>
          {total === 0 ? (
            <p className={s.countLine}>No active students — everyone is archived.</p>
          ) : activeFiltered.length === 0 ? (
            <div className={s.empty}>
              <h2 className={s.emptyTitle}>
                {everyoneBatched ? 'Everyone is in a batch' : 'No matches'}
              </h2>
              <p className={s.emptyBody}>
                {everyoneBatched
                  ? filtersActiveBesidesBatch
                    ? 'Every student matching these filters already belongs to a batch.'
                    : 'Every active student already belongs to at least one batch.'
                  : 'Try a different search term or clear the filters.'}
              </p>
              {filtersActive ? (
                <div className={s.emptyActions}>
                  <Button variant="secondary" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <p className={s.countLine}>
                {showingFiltered
                  ? `Showing ${activeFiltered.length} of ${total} students`
                  : `${total} student${total === 1 ? '' : 's'}`}
              </p>
              <ul className={s.list}>
                {activeFiltered.map((row) => (
                  <StudentRow
                    key={row.id}
                    row={row}
                    busy={pendingId === row.id}
                    onArchive={() => requestArchive(row)}
                    onResetPassword={
                      row.login_kind === 'credential' ? () => requestReset(row) : undefined
                    }
                  />
                ))}
              </ul>
            </>
          )}

          {archived.length > 0 ? (
            <section className={s.archivedSection}>
              <button
                type="button"
                className={s.archiveToggle}
                aria-expanded={showArchived}
                onClick={() => setShowArchived((v) => !v)}
              >
                <Archive size={14} aria-hidden="true" />
                {showArchived ? 'Hide' : 'Show'} archived ({archived.length})
              </button>

              {showArchived ? (
                archivedFiltered.length === 0 ? (
                  <p className={s.archivedEmpty}>No archived students match these filters.</p>
                ) : (
                  <ul className={`${s.list ?? ''} ${s.archivedList ?? ''}`.trim()}>
                    {archivedFiltered.map((row) => (
                      <StudentRow
                        key={row.id}
                        row={row}
                        busy={pendingId === row.id}
                        onRestore={() => restore(row)}
                      />
                    ))}
                  </ul>
                )
              ) : null}
            </section>
          ) : null}
        </>
      )}

      <AddStudentDialog
        instituteId={instituteId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
      <ImportStudentsDialog
        instituteId={instituteId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
      <ArchiveStudentDialog
        studentName={confirmTarget !== null ? displayNameFor(confirmTarget) : null}
        open={confirmTarget !== null}
        pending={archiveMutation.isPending && confirmTarget !== null}
        error={confirmTarget !== null ? actionError : null}
        onConfirm={confirmArchive}
        onClose={() => {
          if (archiveMutation.isPending) return;
          setConfirmTarget(null);
          setActionError(null);
        }}
      />
      <ResetPasswordDialog
        target={resetTarget}
        open={resetTarget !== null}
        onClose={() => setResetTarget(null)}
      />
    </div>
  );
}

interface StudentRowProps {
  row: InstituteStudentRow;
  busy: boolean;
  onArchive?: (() => void) | undefined;
  onRestore?: (() => void) | undefined;
  onResetPassword?: (() => void) | undefined;
}

function StudentRow({
  row,
  busy,
  onArchive,
  onRestore,
  onResetPassword,
}: StudentRowProps): JSX.Element {
  const isArchived = row.archived_at !== null;
  const name = displayNameFor(row);
  const isCredential = row.login_kind === 'credential';
  // Credential students have a synthetic, undeliverable email — show their
  // username instead so the roster stays legible.
  const contact = isCredential ? (row.login_handle ?? '') : row.email;

  return (
    <li className={`${s.row ?? ''} ${isArchived ? (s.rowArchived ?? '') : ''}`.trim()}>
      <span className={s.avatar} aria-hidden="true">
        {initialsFromNameOrEmail(row.display_name, contact)}
      </span>
      <div className={s.rowMain}>
        <div className={s.rowTop}>
          <Link to={`/institute/students/${row.id}/dossier`} className={s.rowName}>
            {name}
          </Link>
          {isArchived ? (
            <span className={s.archivedBadge}>Archived</span>
          ) : (
            <>
              <span
                className={row.status === 'joined' ? s.statusJoined : s.statusInvited}
                title={
                  row.status === 'joined' && row.last_sign_in_at !== null
                    ? `Last sign-in ${new Date(row.last_sign_in_at).toLocaleString()}`
                    : isCredential
                      ? 'Login created; awaiting first sign-in'
                      : 'Magic-link invite sent; awaiting first sign-in'
                }
              >
                {row.status === 'joined' ? 'Joined' : isCredential ? 'Not signed in' : 'Invited'}
              </span>
              {isCredential && row.login_handle !== null ? (
                <span className={s.rowChipHandle}>@{row.login_handle}</span>
              ) : null}
              <span className={s.rowChip}>{row.exam_type}</span>
              {row.roll_no !== null && row.roll_no !== '' ? (
                <span className={s.rowChipSubtle}>roll {row.roll_no}</span>
              ) : null}
            </>
          )}
        </div>
        <div className={s.rowBottom}>
          <span>{contact}</span>
          {row.phone !== '' ? <span>· {row.phone}</span> : null}
          <span className={s.rowJoined}>
            · joined{' '}
            <time dateTime={row.created_at}>{dayjs(row.created_at).format('D MMM YYYY')}</time>
          </span>
        </div>
      </div>
      {row.target_band !== null && !isArchived ? (
        <span className={s.rowMeta}>target {row.target_band}</span>
      ) : null}
      <div className={s.rowActions}>
        {isArchived ? (
          <button
            type="button"
            className={s.actionBtn}
            disabled={busy}
            aria-label={`Restore ${name}`}
            onClick={onRestore}
          >
            {busy ? (
              <Loader2 size={14} className={s.spin} aria-hidden="true" />
            ) : (
              <ArchiveRestore size={14} aria-hidden="true" />
            )}
            Restore
          </button>
        ) : (
          <>
            {onResetPassword !== undefined ? (
              <button
                type="button"
                className={s.actionBtn}
                disabled={busy}
                aria-label={`Reset password for ${name}`}
                onClick={onResetPassword}
              >
                <KeyRound size={14} aria-hidden="true" />
                Reset
              </button>
            ) : null}
            <button
              type="button"
              className={`${s.actionBtn ?? ''} ${s.actionBtnDanger ?? ''}`.trim()}
              disabled={busy}
              aria-label={`Archive ${name}`}
              onClick={onArchive}
            >
              {busy ? (
                <Loader2 size={14} className={s.spin} aria-hidden="true" />
              ) : (
                <Archive size={14} aria-hidden="true" />
              )}
              Archive
            </button>
          </>
        )}
      </div>
    </li>
  );
}
